import React, { useState } from 'react';
import { Transaction } from '../../types';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { formatCurrency, formatDate } from '../../lib/utils';
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { cn } from '../../lib/utils';
interface TransactionTableProps {
  data: Transaction[];
  isLoading?: boolean;
}
type SortField = 'date' | 'amount' | 'cashback' | 'status' | 'id';
type SortDirection = 'asc' | 'desc';
export function TransactionTable({
  data,
  isLoading
}: TransactionTableProps) {
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };
  const sortedData = [...data].sort((a, b) => {
    const multiplier = sortDirection === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'date':
        return multiplier * (new Date(a.date).getTime() - new Date(b.date).getTime());
      case 'amount':
        return multiplier * (a.amount - b.amount);
      case 'cashback':
        return multiplier * (a.cashback - b.cashback);
      case 'status':
        return multiplier * a.status.localeCompare(b.status);
      case 'id':
        return multiplier * a.id.localeCompare(b.id);
      default:
        return 0;
    }
  });
  const totalPages = Math.ceil(sortedData.length / pageSize);
  const paginatedData = sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const getLocationName = (id: string) => {
    const map: Record<string, string> = {
      loc_1: 'Head Office - NY',
      loc_2: 'Brooklyn Branch',
      loc_3: 'Queens Branch',
      loc_4: 'Staten Island'
    };
    return map[id] || id;
  };
  const SortIcon = ({
    field
  }: {
    field: SortField;
  }) => {
    if (sortField !== field) return <div className="w-4 h-4" data-id="element-173" />;
    return sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" data-id="element-174" /> : <ChevronDown className="w-4 h-4" data-id="element-175" />;
  };
  return <div className="space-y-4" data-id="element-176">
      <div className="rounded-md border border-slate-200 bg-white overflow-hidden" data-id="element-177">
        <div className="overflow-x-auto" data-id="element-178">
          <table className="w-full text-sm text-left" data-id="element-179">
            <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-180">
              <tr data-id="element-181">
                <th className="px-4 py-3 cursor-pointer hover:bg-slate-100" onClick={() => handleSort('date')} data-id="element-182">
                  <div className="flex items-center gap-1" data-id="element-183">
                    Date <SortIcon field="date" data-id="element-184" />
                  </div>
                </th>
                <th className="px-4 py-3 cursor-pointer hover:bg-slate-100" onClick={() => handleSort('id')} data-id="element-185">
                  <div className="flex items-center gap-1" data-id="element-186">
                    Transaction ID <SortIcon field="id" data-id="element-187" />
                  </div>
                </th>
                <th className="px-4 py-3" data-id="element-188">Customer</th>
                <th className="px-4 py-3 cursor-pointer hover:bg-slate-100" onClick={() => handleSort('amount')} data-id="element-189">
                  <div className="flex items-center gap-1" data-id="element-190">
                    Amount <SortIcon field="amount" data-id="element-191" />
                  </div>
                </th>
                <th className="px-4 py-3 cursor-pointer hover:bg-slate-100" onClick={() => handleSort('cashback')} data-id="element-192">
                  <div className="flex items-center gap-1" data-id="element-193">
                    Cashback <SortIcon field="cashback" data-id="element-194" />
                  </div>
                </th>
                <th className="px-4 py-3 cursor-pointer hover:bg-slate-100" onClick={() => handleSort('status')} data-id="element-195">
                  <div className="flex items-center gap-1" data-id="element-196">
                    Status <SortIcon field="status" data-id="element-197" />
                  </div>
                </th>
                <th className="px-4 py-3" data-id="element-198">Location</th>
                <th className="px-4 py-3" data-id="element-199"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-200">
              {paginatedData.length === 0 ? <tr data-id="element-201">
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-500" data-id="element-202">
                    No transactions found matching your filters.
                  </td>
                </tr> : paginatedData.map(txn => <>
                    <tr key={txn.id} className="hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => toggleRow(txn.id)} data-id="element-203">
                      <td className="px-4 py-3 font-medium text-slate-900 whitespace-nowrap" data-id="element-204">
                        {formatDate(txn.date)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500" data-id="element-205">
                        {txn.id}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500" title={txn.customerHash} data-id="element-206">
                        {txn.customerHash.substring(0, 8)}...
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-900" data-id="element-207">
                        {formatCurrency(txn.amount)}
                      </td>
                      <td className="px-4 py-3 text-green-600 font-medium" data-id="element-208">
                        {formatCurrency(txn.cashback)}
                      </td>
                      <td className="px-4 py-3" data-id="element-209">
                        <Badge variant={txn.status === 'paid' ? 'success' : txn.status === 'failed' ? 'destructive' : txn.status === 'processed' ? 'warning' : 'info'} data-id="element-210">
                          {txn.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs" data-id="element-211">
                        {getLocationName(txn.locationId)}
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-212">
                        <ChevronDown className={cn('h-4 w-4 text-slate-400 transition-transform', expandedRows.has(txn.id) && 'rotate-180')} data-id="element-213" />
                      </td>
                    </tr>
                    {expandedRows.has(txn.id) && <tr className="bg-slate-50/50" data-id="element-214">
                        <td colSpan={8} className="px-4 py-4" data-id="element-215">
                          <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-216">
                            <div data-id="element-217">
                              <p className="text-xs font-medium text-slate-500 uppercase mb-1" data-id="element-218">
                                Transaction Details
                              </p>
                              <div className="space-y-1" data-id="element-219">
                                <p data-id="element-220">
                                  <span className="text-slate-500" data-id="element-221">
                                    Full ID:
                                  </span>{' '}
                                  <span className="font-mono" data-id="element-222">{txn.id}</span>
                                </p>
                                <p data-id="element-223">
                                  <span className="text-slate-500" data-id="element-224">
                                    Timestamp:
                                  </span>{' '}
                                  {new Date(txn.date).toLocaleString()}
                                </p>
                                <p data-id="element-225">
                                  <span className="text-slate-500" data-id="element-226">
                                    Location ID:
                                  </span>{' '}
                                  <span className="font-mono" data-id="element-227">
                                    {txn.locationId}
                                  </span>
                                </p>
                              </div>
                            </div>
                            <div data-id="element-228">
                              <p className="text-xs font-medium text-slate-500 uppercase mb-1" data-id="element-229">
                                Financials
                              </p>
                              <div className="space-y-1" data-id="element-230">
                                <p data-id="element-231">
                                  <span className="text-slate-500" data-id="element-232">
                                    Amount:
                                  </span>{' '}
                                  {formatCurrency(txn.amount)}
                                </p>
                                <p data-id="element-233">
                                  <span className="text-slate-500" data-id="element-234">
                                    Cashback to Customer:
                                  </span>{' '}
                                  {formatCurrency(txn.cashback)}
                                </p>
                                <p data-id="element-235">
                                  <span className="text-slate-500" data-id="element-236">
                                    Cashback Rate:
                                  </span>{' '}
                                  7.5%
                                </p>
                              </div>
                            </div>
                            <div data-id="element-237">
                              <p className="text-xs font-medium text-slate-500 uppercase mb-1" data-id="element-238">
                                Customer
                              </p>
                              <div className="space-y-1" data-id="element-239">
                                <p data-id="element-240">
                                  <span className="text-slate-500" data-id="element-241">Hash:</span>{' '}
                                  <span className="font-mono" data-id="element-242">
                                    {txn.customerHash}
                                  </span>
                                </p>
                                <p data-id="element-243">
                                  <span className="text-slate-500" data-id="element-244">
                                    Status:
                                  </span>{' '}
                                  Verified
                                </p>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>}
                  </>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-2" data-id="element-245">
        <div className="text-sm text-slate-500" data-id="element-246">
          Showing{' '}
          <span className="font-medium" data-id="element-247">
            {Math.min((currentPage - 1) * pageSize + 1, sortedData.length)}
          </span>{' '}
          to{' '}
          <span className="font-medium" data-id="element-248">
            {Math.min(currentPage * pageSize, sortedData.length)}
          </span>{' '}
          of <span className="font-medium" data-id="element-249">{sortedData.length}</span> results
        </div>
        <div className="flex items-center gap-2" data-id="element-250">
          <select className="h-8 w-16 rounded border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#218080]" value={pageSize} onChange={e => {
          setPageSize(Number(e.target.value));
          setCurrentPage(1);
        }} data-id="element-251">
            <option value={20} data-id="element-252">20</option>
            <option value={50} data-id="element-253">50</option>
            <option value={100} data-id="element-254">100</option>
          </select>
          <div className="flex gap-1" data-id="element-255">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} data-id="element-256">
              <ChevronsLeft className="h-4 w-4" data-id="element-257" />
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} data-id="element-258">
              <ChevronLeft className="h-4 w-4" data-id="element-259" />
            </Button>
            <span className="flex items-center justify-center h-8 min-w-[2rem] text-sm font-medium" data-id="element-260">
              {currentPage} / {totalPages}
            </span>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} data-id="element-261">
              <ChevronRight className="h-4 w-4" data-id="element-262" />
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} data-id="element-263">
              <ChevronsRight className="h-4 w-4" data-id="element-264" />
            </Button>
          </div>
        </div>
      </div>
    </div>;
}